import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Scrollbars } from 'react-custom-scrollbars-2';
import { X } from 'lucide-react';
import styles from './SingleEbook.module.css';
import scrollStyles from './SingleEbook.module.css';
import { useNavigate, useParams } from 'react-router-dom';
import { useEbookBySlug } from '../../hooks/useEbooksHooks';
import Loading from '../../common/HousGlass Loader/Loading';
import { Helmet } from 'react-helmet';

// Add helper functions for pagination
const countWords = (text) => text.trim().split(/\s+/).filter(word => word.length > 0).length;
const splitParagraphIntoSentences = (text) => {
  return text.match(/[^.!?]+[.!?]+(\s|$)|[^.!?]+$/g) || [text];
};

function splitContentIntoPages(htmlContent, wordsPerPage = 250) {
  const parser = new DOMParser();
  const doc = parser.parseFromString(`<div>${htmlContent}</div>`, 'text/html');
  const container = doc.querySelector('div');
  const pages = [];
  let currentPage = document.createElement('div');
  let currentWordCount = 0;

  Array.from(container.childNodes).forEach(node => {
    if (node.nodeType === Node.ELEMENT_NODE && ['P','H1','H2','H3','H4','H5','H6','UL','OL','LI','DIV','TABLE','BLOCKQUOTE','PRE'].includes(node.tagName)) {
      const text = node.textContent || '';
      const wordCount = countWords(text);
      if (currentWordCount + wordCount <= wordsPerPage) {
        currentPage.appendChild(node.cloneNode(true));
        currentWordCount += wordCount;
      } else if (wordCount > wordsPerPage) {
        // Block is too big, split by sentences
        const sentences = splitParagraphIntoSentences(text);
        let tempNode = document.createElement(node.tagName);
        let tempCount = 0;
        sentences.forEach(sentence => {
          const sentenceWordCount = countWords(sentence);
          if (currentWordCount + tempCount + sentenceWordCount > wordsPerPage && tempNode.innerHTML.trim()) {
            currentPage.appendChild(tempNode.cloneNode(true));
            pages.push({ htmlContent: currentPage.innerHTML, wordCount: currentWordCount + tempCount });
            currentPage = document.createElement('div');
            tempNode = document.createElement(node.tagName);
            tempCount = 0;
            currentWordCount = 0;
          }
          if (tempNode.innerHTML) tempNode.innerHTML += ' ';
          tempNode.innerHTML += sentence.trim();
          tempCount += sentenceWordCount;
        });
        if (tempNode.innerHTML.trim()) {
          currentPage.appendChild(tempNode.cloneNode(true));
          currentWordCount += tempCount;
        }
      } else {
        // Start a new page for this block
        if (currentPage.innerHTML.trim()) {
          pages.push({ htmlContent: currentPage.innerHTML, wordCount: currentWordCount });
        }
        currentPage = document.createElement('div');
        currentPage.appendChild(node.cloneNode(true));
        currentWordCount = wordCount;
      }
    } else if (node.nodeType === Node.ELEMENT_NODE) {
      currentPage.appendChild(node.cloneNode(true));
    } else if (node.nodeType === Node.TEXT_NODE) {
      const text = node.textContent || '';
      const wordCount = countWords(text);
      if (currentWordCount + wordCount > wordsPerPage && currentPage.innerHTML.trim()) {
        pages.push({ htmlContent: currentPage.innerHTML, wordCount: currentWordCount });
        currentPage = document.createElement('div');
        currentWordCount = 0;
      }
      currentPage.appendChild(document.createTextNode(text));
      currentWordCount += wordCount;
    }
  });
  if (currentPage.innerHTML.trim()) {
    pages.push({ htmlContent: currentPage.innerHTML, wordCount: currentWordCount });
  }
  return pages;
}

function SingleBookPreview() {
  const [animating, setAnimating] = useState(false);
  const [scrollPosition, setScrollPosition] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [initialClickOffset, setInitialClickOffset] = useState(0);
  
  const scrollbarRef = useRef(null);
  const scrollContainerRef = useRef(null);
  const circleRef = useRef(null);
  
  // Get route parameters
  const { slug } = useParams();
  const navigate = useNavigate();
  
  // Get ebook data from hook
  const { ebook, isLoading, error } = useEbookBySlug(slug);
  
  // Define navigation function to go back
  const navigateBack = useCallback(() => {
    navigate(`/book-detail/${ebook?.slug}`);
  }, [navigate, ebook]);

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        navigateBack();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [navigateBack]);

  // Update scroll position when content is scrolled
  const handleContentScroll = ({ top }) => {
    if (!isDragging && scrollContainerRef.current) {
      const containerHeight = scrollContainerRef.current.clientHeight;
      const circleHeight = 16;
      const maxPosition = containerHeight - circleHeight;
      setScrollPosition(top * maxPosition);
    }
  };

  // Handle mouse down on the circle
  const handleMouseDown = (e) => {
    e.preventDefault();
    
    if (circleRef.current && scrollContainerRef.current) {
      const circleRect = circleRef.current.getBoundingClientRect();
      
      // Calculate the offset of the click within the circle
      // This ensures we grab the circle exactly where the user clicked
      const offsetY = e.clientY - circleRect.top;
      setInitialClickOffset(offsetY);
      
      setIsDragging(true);
    }
  };

  // Handle mouse move (dragging)
  const handleMouseMove = useCallback((e) => {
    if (isDragging && scrollContainerRef.current && scrollbarRef.current) {
      const containerRect = scrollContainerRef.current.getBoundingClientRect();
      const circleHeight = 16;
      
      // Calculate new position, accounting for the initial click offset
      // within the circle to make dragging feel natural
      let newY = e.clientY - containerRect.top - initialClickOffset;
      
      const maxY = containerRect.height - circleHeight;
      
      // Constrain within bounds
      newY = Math.max(0, Math.min(newY, maxY));
      
      // Update circle position
      setScrollPosition(newY);
      
      // Calculate scroll percentage and update content scroll position
      const scrollPercentage = newY / maxY;
      if (scrollbarRef.current) {
        scrollbarRef.current.scrollTop(scrollPercentage * scrollbarRef.current.getScrollHeight());
      }
    }
  }, [isDragging, initialClickOffset]);

  // Handle mouse up
  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);
  
  // Handle mouse wheel on scroll container
  const handleMouseWheel = useCallback((e) => {
    if (scrollbarRef.current && scrollContainerRef.current) {
      e.preventDefault();
      
      // Determine scroll direction and adjust speed
      const delta = e.deltaY || e.detail || (e.wheelDelta * -1); // Normalize wheel delta across browsers
      const scrollSpeed = 0.9; // Fast scrolling
      
      // Get current scroll position and calculate new position
      const currentTop = scrollbarRef.current.getScrollTop();
      const scrollHeight = scrollbarRef.current.getScrollHeight();
      const clientHeight = scrollbarRef.current.getClientHeight();
      
      // Calculate new scroll position
      const newScrollTop = currentTop + (delta * scrollSpeed);
      
      // Apply the scroll
      scrollbarRef.current.scrollTop(newScrollTop);
      
      // Update custom scrollbar position
      const containerHeight = scrollContainerRef.current.clientHeight;
      const circleHeight = 16;
      const maxPosition = containerHeight - circleHeight;
      const scrollPercentage = newScrollTop / (scrollHeight - clientHeight);
      
      // Ensure the scroll percentage is within bounds
      const boundedPercentage = Math.max(0, Math.min(1, scrollPercentage));
      setScrollPosition(boundedPercentage * maxPosition);
    }
  }, []);

  // Add/remove event listeners for mouse move and up
  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    } else {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    }
    
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, handleMouseMove, handleMouseUp]);
  
  // Set up mouse wheel event listeners immediately after render
  // and whenever the component updates
  useEffect(() => {
    // Set a small timeout to ensure the DOM is fully rendered
    const timer = setTimeout(() => {
      const scrollContainer = scrollContainerRef.current;
      
      if (scrollContainer) {
        // First remove any existing listeners to prevent duplicates
        scrollContainer.removeEventListener('wheel', handleMouseWheel);
        scrollContainer.removeEventListener('DOMMouseScroll', handleMouseWheel);
        
        // Then add the listeners again
        scrollContainer.addEventListener('wheel', handleMouseWheel, { passive: false });
        scrollContainer.addEventListener('DOMMouseScroll', handleMouseWheel, { passive: false });
        
        // Also add mousewheel event for older IE/Edge
        scrollContainer.addEventListener('mousewheel', handleMouseWheel, { passive: false });
      }
    }, 100);
    
    return () => {
      clearTimeout(timer);
      const scrollContainer = scrollContainerRef.current;
      if (scrollContainer) {
        scrollContainer.removeEventListener('wheel', handleMouseWheel);
        scrollContainer.removeEventListener('DOMMouseScroll', handleMouseWheel);
        scrollContainer.removeEventListener('mousewheel', handleMouseWheel);
      }
    };
  }, [handleMouseWheel, ebook]); // Added ebook as dependency

  // Format content to handle HTML
  const formatContent = (content) => {
    return { __html: content };
  };

  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  // Memoize pages for performance
  const pages = useMemo(() => {
    if (!ebook?.full_content) return [];
    const contentPages = splitContentIntoPages(ebook.full_content, 250);
    setTotalPages(contentPages.length);
    return contentPages;
  }, [ebook]);

  // Loading state
  if (isLoading || !ebook) {
    return <div className={styles.loading}><Loading/></div>;
  }

  // Error state
  if (error) {
    return <div className={styles.loading}>Error loading book: {error.message}</div>;
  }

  return (
    <>
    <Helmet>
      <title>{ebook.title} - SunoKahaniyan</title>
      <meta name="description" content={ebook.description || "Read this book on SunoKahaniyan"} />
    </Helmet>
    <div className={styles.bookContainer}>
      {/* Main reading area */}
      <div className={styles.readerContainer}>
        {/* Top navigation */}
        <div className={styles.readerHeader}>
          <div className={styles.bookInfo}>
            <h2 className={styles.bookTitle}>{ebook.title}</h2>
            <p className={styles.authorInfo}>by {ebook.author}</p>
          </div>
          <div className={styles.genreInfo}>
            {ebook.genre_name}
          </div>
          <button className={styles.backButton} onClick={navigateBack}>
            <X size={24} />
          </button>
        </div>

        {/* Book content */}
        <div className={styles.contentContainer}>
          <Scrollbars 
            universal
            ref={scrollbarRef}
            className={`${styles.contentScrollbar} ${scrollStyles.customScrollbar}`}
            renderThumbVertical={props => <div {...props} className={scrollStyles.customThumb} />}
            onScrollFrame={handleContentScroll}
          >
            <div 
              className={`${styles.bookContent} ${animating ? styles.fadeOut : styles.fadeIn}`}
              style={{ fontFamily: "'Raleway', sans-serif" }}
            >
              <h1 
                className={styles.contentBookTitle} 
                style={{ fontFamily: "'Cinzel Decorative', serif" }}
              >
                {ebook.title}
              </h1>
              {pages.length > 0 ? (
                <div className={styles.bookFullContent} dangerouslySetInnerHTML={{ __html: pages[currentPage]?.htmlContent || '' }} />
              ) : (
                <div className={styles.bookFullContent} dangerouslySetInnerHTML={formatContent(ebook.full_content)} />
              )}
            </div>
          </Scrollbars>
          
          {/* Custom scroll indicator */}
          <div 
            className={scrollStyles.scrollContainer} 
            ref={scrollContainerRef}
            onClick={(e) => {
              // Handle direct clicks on the scrollbar track
              if (scrollContainerRef.current && scrollbarRef.current) {
                const containerRect = scrollContainerRef.current.getBoundingClientRect();
                const circleHeight = 16;
                
                // Calculate position relative to container
                let newY = e.clientY - containerRect.top - (circleHeight / 2);
                const maxY = containerRect.height - circleHeight;
                
                // Constrain within bounds
                newY = Math.max(0, Math.min(newY, maxY));
                
                // Update circle position
                setScrollPosition(newY);
                
                // Update content scroll position
                const scrollPercentage = newY / maxY;
                scrollbarRef.current.scrollTop(scrollPercentage * scrollbarRef.current.getScrollHeight());
              }
            }}
          >
            <div className={scrollStyles.scrollLine}></div>
            <div 
              ref={circleRef}
              className={scrollStyles.scrollCircle} 
              style={{ top: `${scrollPosition}px` }}
              onMouseDown={handleMouseDown}
            ></div>
          </div>

          {/* Add pagination controls below the content (inside the Scrollbars, after the content) */}
          <div className={styles.paginationControls}>
            <button
              onClick={() => setCurrentPage((p) => Math.max(0, p - 1))}
              disabled={currentPage === 0}
              className={styles.pageButton}
            >
              Previous
            </button>
            <span className={styles.pageInfo}>
              Page {currentPage + 1} of {totalPages}
            </span>
            <button
              onClick={() => setCurrentPage((p) => Math.min(totalPages - 1, p + 1))}
              disabled={currentPage >= totalPages - 1}
              className={styles.pageButton}
            >
              Next
            </button>
          </div>
        </div>
      </div>
    </div>
    </>
  );
}

export default SingleBookPreview;